import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { AURORA_THEME } from "./theme";

// Dark-mode FAQ accordion for /c/aurora. Mirrors the shared FAQs component
// behaviour but with a deep-space surface, aurora-green accents, and high-contrast
// text. The shared component hardcodes #F8F9FA / #E9ECEF, which renders as a
// bright white card on the dark backdrop.

const AuroraFAQs = ({ customFaqs }) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="tw-w-full">
      <div className="tw-text-center">
        <h2
          className="tw-text-2xl tw-font-extrabold md:tw-text-3xl"
          style={{ color: AURORA_THEME.text.primary }}
        >
          {t("landing.faqs.title")}
        </h2>
        <p
          className="tw-mt-2 tw-text-sm tw-font-semibold md:tw-text-base"
          style={{ color: AURORA_THEME.accent.primary }}
        >
          {t("landing.faqs.subtitle")}
        </p>
      </div>

      <div className="tw-mx-auto tw-mt-8 md:tw-max-w-3xl">
        {customFaqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className={`tw-overflow-hidden ${
                index === 0 ? "tw-rounded-t-lg" : ""
              } ${index === customFaqs.length - 1 ? "tw-rounded-b-lg" : ""}`}
              style={{
                borderTop: index === 0 ? "1px solid rgba(0,255,204,0.15)" : "none",
                borderLeft: "1px solid rgba(0,255,204,0.15)",
                borderRight: "1px solid rgba(0,255,204,0.15)",
                borderBottom: "1px solid rgba(0,255,204,0.15)",
                backgroundColor: AURORA_THEME.bg.surface,
              }}
            >
              <button
                onClick={() => toggle(index)}
                className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left tw-transition-colors md:tw-px-6 md:tw-py-4"
                style={{
                  backgroundColor: isOpen
                    ? AURORA_THEME.bg.elevated
                    : "transparent",
                  color: AURORA_THEME.text.primary,
                }}
              >
                <div className="tw-grow tw-text-sm tw-font-semibold md:tw-text-base">
                  {faq.question}
                </div>
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="tw-flex-none"
                  style={{ color: AURORA_THEME.accent.primary }}
                >
                  <ChevronDown className="tw-h-5 tw-w-5 md:tw-h-6 md:tw-w-6" />
                </motion.div>
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="tw-overflow-hidden"
                  >
                    <div
                      className="tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed md:tw-px-6 md:tw-py-4 md:tw-text-base"
                      style={{
                        borderTop: "1px solid rgba(0,255,204,0.12)",
                        backgroundColor: AURORA_THEME.bg.elevated,
                        color: AURORA_THEME.text.secondary,
                      }}
                    >
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuroraFAQs;
